/**
 * Pure helpers that map a stream of {@link PitchEvent}s to ear-training
 * answers. No audio APIs, no DOM. The detector produces the events; this
 * module decides what they *mean*.
 *
 * Three levels:
 *
 *   detectHeldPitch(events)       — frame stream → "the user is sustaining
 *                                    this MIDI" snapshot, or null
 *   gradeScaleDegree(midi, key)   — held MIDI → scale degree in a key
 *   gradeInterval(a, b)           — two consecutive holds → interval label
 *
 * Per CLAUDE.md, scale-degree integration ships in C.1; interval grading
 * ships *as a helper* in C.1 but its Practice integration is deferred.
 */

import { intervalToString, parseInterval } from "@music-app/theory-engine";

export interface PitchEvent {
  midi: number;
  cents: number;
  clarity: number;
  timestamp: number;
}

export interface HeldPitch {
  /** Mean MIDI across the held window, rounded to nearest integer. */
  midi: number;
  /** Mean cents deviation across the held window. */
  meanCents: number;
  /** Mean clarity across the held window. */
  meanClarity: number;
  /** Total span of the held window, milliseconds. */
  durationMs: number;
}

export interface DetectHeldPitchOptions {
  /** Minimum sustained duration to qualify as "held", in ms. Default 200. */
  minHoldMs?: number;
  /** Minimum mean clarity. Default 0.9 (matches spec §9.3). */
  clarityThreshold?: number;
  /** Per-frame deviation tolerance, in cents. Default 50. */
  centsTolerance?: number;
}

const DEFAULT_HELD_OPTS: Required<DetectHeldPitchOptions> = {
  minHoldMs: 200,
  clarityThreshold: 0.9,
  centsTolerance: 50,
};

/**
 * Inspect a window of recent pitch events and return a "held pitch"
 * snapshot if the user has been sustaining a single semitone for
 * ≥ minHoldMs at clarity ≥ clarityThreshold, with every frame within
 * ±centsTolerance of that semitone. Returns null otherwise.
 *
 * The window walked from the END backward (most recent frame first), so
 * this is cheap to call from a tight render loop with a small ring
 * buffer of recent events. The held semitone is taken from the most
 * recent frame; older frames must agree (within tolerance) for the
 * window to be considered stable.
 */
export function detectHeldPitch(
  events: ReadonlyArray<PitchEvent>,
  opts?: DetectHeldPitchOptions,
): HeldPitch | null {
  const o = { ...DEFAULT_HELD_OPTS, ...opts };
  if (events.length === 0) return null;

  // Newest frame anchors the candidate semitone. If even that frame is
  // below clarity, give up early.
  const newest = events[events.length - 1]!;
  if (newest.clarity < o.clarityThreshold) return null;
  const candidate = Math.round(newest.midi);

  // Walk backward, accumulating until we either drop out of tolerance,
  // drop below clarity, or run out of buffer. Stop accumulating when
  // we've spanned `minHoldMs` worth of timestamps from the most recent
  // frame; further history isn't needed.
  let countedFrames = 0;
  let centsSum = 0;
  let claritySum = 0;
  let earliestTs = newest.timestamp;
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i]!;
    const span = (newest.timestamp - ev.timestamp) * 1000; // s → ms
    // The newest frame itself contributes (span = 0). We always include
    // it before evaluating the duration check.
    if (ev.clarity < o.clarityThreshold) break;
    const semitoneOffset = ev.midi - candidate;
    const cents = semitoneOffset * 100;
    if (Math.abs(cents) > o.centsTolerance) break;
    countedFrames++;
    centsSum += cents;
    claritySum += ev.clarity;
    earliestTs = ev.timestamp;
    if (span >= o.minHoldMs) {
      // We've satisfied the window — extra history is fine but we don't
      // need to keep walking.
      break;
    }
  }

  const durationMs = (newest.timestamp - earliestTs) * 1000;
  if (durationMs < o.minHoldMs) return null;
  if (countedFrames === 0) return null;

  return {
    midi: candidate,
    meanCents: centsSum / countedFrames,
    meanClarity: claritySum / countedFrames,
    durationMs,
  };
}

/* ─── Scale-degree grading ───────────────────────────────────────────── */

/** Movable-do mapping: which scale degree corresponds to which semitone
 *  offset above the tonic, for major and minor. Octave-agnostic by design
 *  — only pitch class matters. Degree numbers are the same for both
 *  do-based and la-based minor; only the syllable names differ. */
const MAJOR_DEGREE_BY_SEMITONE: Record<number, number> = {
  0: 1, 2: 2, 4: 3, 5: 4, 7: 5, 9: 6, 11: 7,
};
const MINOR_DEGREE_BY_SEMITONE: Record<number, number> = {
  // Natural minor (Aeolian): 0 W H W W H W W.
  0: 1, 2: 2, 3: 3, 5: 4, 7: 5, 8: 6, 10: 7,
};

export interface ScaleDegreeMatch {
  degree: number;
  /** Set when the matched note is a chromatic alteration of a diatonic
   *  degree (e.g. raised 4 = "♯", lowered 6 = "♭"). Absent when the
   *  match is a plain diatonic degree. */
  alteration?: "♯" | "♭";
}

export interface GradeScaleDegreeOptions {
  /** Accept chromatic neighbors of diatonic degrees. Default false —
   *  the spec calls for chromatic recognition as its own exercise type. */
  allowChromatic?: boolean;
  /** Tolerance around an integer semitone, in cents. Default 50 (the
   *  full ±half-semitone window — anything closer to the next semitone
   *  rounds *to* the next semitone, not this one). */
  centsTolerance?: number;
}

/**
 * Map a held MIDI pitch to a scale degree (1..7) in a key.
 *
 * Octave-agnostic: only the pitch class matters. So if the user sings
 * "do" an octave below the reference tonic, this still resolves to
 * degree 1. That matches spec §9.4 "Sustained correct pitch but
 * transposed by an octave → Accept".
 *
 * Returns null if the heard pitch is not a scale tone (or, when
 * `allowChromatic` is false, not a *diatonic* scale tone).
 *
 * @param heldMidi — the rounded MIDI value from detectHeldPitch
 * @param keyTonicMidi — MIDI for the key's tonic at any octave
 * @param mode — "major" or "minor"
 */
export function gradeScaleDegree(
  heldMidi: number,
  keyTonicMidi: number,
  mode: "major" | "minor",
  opts?: GradeScaleDegreeOptions,
): ScaleDegreeMatch | null {
  const allowChromatic = opts?.allowChromatic ?? false;
  // Pitch-class delta in [0, 11].
  const offset = ((Math.round(heldMidi) - Math.round(keyTonicMidi)) % 12 + 12) % 12;
  const table =
    mode === "major" ? MAJOR_DEGREE_BY_SEMITONE : MINOR_DEGREE_BY_SEMITONE;

  const direct = table[offset];
  if (direct !== undefined) {
    return { degree: direct };
  }
  if (!allowChromatic) return null;

  // Try chromatic alterations: heard offset = diatonic offset ± 1.
  // Prefer the *raised* spelling when both fit (e.g. ♯4 over ♭5)
  // because raised-4 is the more common musical use.
  const raisedFrom = table[(offset - 1 + 12) % 12];
  if (raisedFrom !== undefined) {
    return { degree: raisedFrom, alteration: "♯" };
  }
  const loweredFrom = table[(offset + 1) % 12];
  if (loweredFrom !== undefined) {
    return { degree: loweredFrom, alteration: "♭" };
  }
  return null;
}

/* ─── Interval grading ───────────────────────────────────────────────── */

export interface IntervalMatch {
  /** Standard interval shorthand: P5, M3, m7, A4, d5, etc. */
  interval: string;
  /** Absolute semitone count between the two pitches. */
  semitones: number;
}

/**
 * Map two consecutive held MIDI pitches to an interval label.
 *
 * Order-sensitive only in that the magnitude is `|second - first|`; the
 * sign is dropped so the grader can be called for either ascending or
 * descending sung answers and get the same name. The diatonic name is
 * preferred (M3 over d4, m3 over A2) because exercises ask for the
 * common-practice spelling.
 *
 * Octave-agnostic intervals (e.g. M9 vs M2) collapse to their simple
 * form here — Phase 2 doesn't drill compound intervals as a separate
 * recognition exercise.
 */
export function gradeInterval(
  firstMidi: number,
  secondMidi: number,
): IntervalMatch | null {
  const semitones = Math.abs(Math.round(secondMidi) - Math.round(firstMidi));
  // Reduce to within an octave. Unison (0) and the octave itself (12)
  // are both legitimate answers; we report the simple form.
  const simple = semitones % 12;
  // Common-practice diatonic spellings, preferred for matching.
  // Multiple intervals can share a semitone count (M3 = d4 = 4 semis);
  // the table picks the diatonic one.
  const preferred: Record<number, string> = {
    0: "P1",
    1: "m2",
    2: "M2",
    3: "m3",
    4: "M3",
    5: "P4",
    6: "A4", // tritone — A4 is the common-practice spelling vs d5
    7: "P5",
    8: "m6",
    9: "M6",
    10: "m7",
    11: "M7",
  };
  const label = simple === 0 && semitones === 12 ? "P8" : preferred[simple];
  if (!label) return null;
  // Sanity-check the label parses back to the same semitones — defends
  // against future table edits that drift out of sync with theory-engine.
  const parsed = parseInterval(label);
  if (!parsed) return null;
  return {
    interval: intervalToString(parsed),
    semitones,
  };
}
